import { getCredentials } from "../lib/auth";
import { lumeTools } from "./tools";

/**
 * The agent service's session and its conversations.
 *
 * THE SERVICE OWNS THREAD IDS. A conversation is created there and comes back with
 * a generated uuid; nothing here composes one. That is what makes "nova conversa"
 * work without any client state: a new conversation is a new thread, the newest
 * thread wins, and the previous one stays where it is as history. A client-derived
 * id (`caderno-7`, then `caderno-7#2`…) would need somewhere to remember which
 * suffix is current — which is the local copy this whole change removed.
 *
 * Threads are found again by METADATA rather than by a name we made up: one tag per
 * surface (`{cadernoId}`, or `{hub:true}`), which the list endpoint filters on. The
 * student never appears in the tag because it never has to — the service resolves
 * the memory resource from the verified session and ignores any resourceId sent from
 * here. Checked, not assumed: a request claiming `resourceId: "999"` created a
 * thread owned by the caller, and one student listing threads sees only their own.
 */

/** What a conversation is about, and how it is found again. */
export type ThreadKey = { cadernoId: number } | { hub: true };

export interface AgentThread {
  id: string;
}

/**
 * What came back when we asked for a thread's messages.
 *
 * "Nothing there" and "could not read it" are SEPARATE on purpose, and collapsing
 * them was a real bug: an unreadable transcript rendered as an empty panel while
 * every turn still went to the same thread, so the student was told they were
 * starting a new conversation and the agent carried on with the old one.
 *
 * `read` with an empty array is a genuinely new conversation. `unreadable` means we
 * do not know what is in the thread — the panel must not imply it is empty.
 */
export type ThreadTranscript =
  | {
      status: "read";
      messages: unknown[];
      /**
       * Rows in the whole thread, as the service counts them — what says whether
       * there is anything older than the page we just read. Null when the response
       * did not carry it, which reads as "no idea", not as "nothing".
       */
      total: number | null;
    }
  | { status: "unreadable" };

/** One past conversation, as the picker lists them. */
export interface ThreadSummary {
  id: string;
  /**
   * What this conversation is about, or null when nothing has named it yet.
   *
   * Mastra titles a thread it creates `New Thread <iso>` and we do not turn its
   * `generateTitle` on, so that string is the ABSENCE of a title rather than one —
   * normalised to null here so the picker never shows it to a student.
   */
  title: string | null;
  /** Last activity, ISO. Null when the service did not say. */
  updatedAt: string | null;
  createdAt: string | null;
}

/**
 * The student's session as a bearer token, or null when they are not logged in.
 *
 * `<uuid>.<token>` — straight off the cookies, which is the point: no id lookup, so
 * this is available synchronously anywhere, including before any query has resolved.
 * The service splits on the first dot and resolves the account from the pair
 * (mastra-service/src/mastra/lume/auth-provider.ts).
 */
export function lumeBearer(): string | null {
  const { uuid, token } = getCredentials();

  if (!uuid || !token) {
    return null;
  }

  return `${uuid}.${token}`;
}

/**
 * How many messages one page of history holds.
 *
 * The drawer opens on the newest page and walks backwards from there as the
 * student scrolls up, so this is a page size and no longer a ceiling on what the
 * conversation can show.
 */
export const HISTORY_PAGE_SIZE = 40;

/** How many past conversations the picker offers. */
export const CONVERSATION_LIMIT = 10;

/** Threads per surface to scan for the newest. A student never has many. */
const THREAD_PAGE = 100;

/**
 * How long the HUB's conversation may sit idle and still be picked up again.
 *
 * Three hours: long enough to cover a sitting — the student opens /lume, goes into a
 * caderno, comes back — and short enough that they are not greeted by a conversation
 * from yesterday.
 *
 * The hub's subject is "which caderno, or what new one", which is a question about
 * right now. Its memory is not free either: with observational memory on, the agent
 * carries the observations plus every message since the last observation, so a thread
 * that is never retired only grows. A caderno's thread has NO window by contrast —
 * that one is about the subject being studied, and a student coming back to Funções
 * after a week should find the conversation they had about Funções.
 *
 * Idle, not age: a conversation in active use is never cut off underneath the student.
 */
const HUB_RESUME_WINDOW_MS = 3 * 60 * 60 * 1000;

/**
 * How long the TUTOR PAGE's conversation may sit idle and still be picked up.
 *
 * One hour, and it exists because the page's first rule was too blunt. It used to
 * start a brand-new thread on every arrival, on the argument that a page you
 * navigate to is a place you arrive at with something to ask — true, and it made
 * the common case worse: leave the Tutor to check a tópico, come back two minutes
 * later, and the conversation you were in the middle of is gone from the screen
 * (into the history, but gone). Every one of those visits also left an empty thread
 * behind.
 *
 * An hour is the span of a sitting. Inside it, coming back is CONTINUING and the
 * conversation is still on screen; outside it, arriving is starting, and last
 * Tuesday's exchange about Cinemática is not an answer to a question nobody just
 * asked. Shorter than the hub's three hours on purpose: the drawer's subject is the
 * caderno and survives a gap, this page's subject is whatever the student walked up
 * with.
 *
 * PER SURFACE AND NOT PER KEY, which is why it is passed in rather than derived
 * below. The drawer and the caderno's Tutor page are one thread key — the same
 * conversation, deliberately — and they want different windows: the drawer resumes a
 * week-old thread about Funções, because that is the point of a tutor that remembers
 * the caderno.
 */
export const TUTOR_RESUME_WINDOW_MS = 60 * 60 * 1000;

/**
 * The default idle window for a surface, or null when its conversations never
 * expire. Overridden by whatever the caller passes to resolveThread.
 */
function defaultResumeWindowMs(key: ThreadKey): number | null {
  return "hub" in key ? HUB_RESUME_WINDOW_MS : null;
}

export interface ThreadEndpoint {
  baseUrl: string | null;
  agentId: string;
}

/**
 * Where the agent service lives, or null when there is none.
 *
 * Emitted by lume.php from LUME_AGENT_URL in config.php — a config value and not a
 * build-time constant, so the same bundle can point at a local service, a staging
 * one, or nothing at all. `import.meta.env.DEV` would have been the obvious thing
 * to key this on and is the wrong thing: it is false in a production build, so a
 * built bundle would silently fall back to the stub with no way to override it.
 *
 * No URL means no agent, which means no memory: every call below reports
 * "unconfigured" and the drawer starts from an empty thread.
 */
export function lumeAgentUrl(): string | null {
  const configured = (window as { __LUME_AGENT_URL__?: unknown })
    .__LUME_AGENT_URL__;

  if (typeof configured === "string" && configured !== "") {
    return configured.replace(/\/$/, "");
  }

  return null;
}

/**
 * The conversation to use for this surface: the newest one, or a fresh one.
 *
 * Null when there is no service or no session, which the caller treats as "no
 * memory" — the drawer still works, it just starts from the welcome every time.
 */
export async function resolveThread(
  endpoint: ThreadEndpoint,
  key: ThreadKey,
  /**
   * How long the newest thread may have been idle and still be resumed. Undefined
   * takes the key's own default — see defaultResumeWindowMs.
   */
  resumeWindowMs?: number
): Promise<AgentThread> {
  const existing = await newestThread(endpoint, key, resumeWindowMs);

  if (existing !== null) {
    return existing;
  }

  // Awaited, not `??`-chained: a Promise is never nullish, so chaining would return
  // the promise itself and the fallback would be dead code — which is how this
  // returned null in the first place
  const created = await createThread(endpoint, key);

  return created ?? fallbackThread(key);
}

/**
 * A thread id of our own, for when the service would not give us one.
 *
 * THERE IS NO "NO THREAD" OPTION. An agent with memory configured refuses a turn
 * that arrives without one — "A resourceId and a threadId must be provided when
 * using Memory. Saw threadId 'undefined'" — so leaving it out does not degrade the
 * conversation, it ends it. This is the difference between a chat that forgets and
 * no chat at all.
 *
 * Derived from the key so it is the SAME id next time: continuity survives even
 * here, and Mastra creates the thread on first use. What is lost is the metadata
 * tag, so this thread will not be found by the list query — meaning rotation
 * ("nova conversa") cannot supersede it, because "newest by tag" will not see it.
 * A degraded mode worth having, and not one to stay in.
 */
function fallbackThread(key: ThreadKey): AgentThread {
  if ("cadernoId" in key) {
    return { id: `caderno-${key.cadernoId}` };
  }

  const { uuid } = getCredentials();

  return { id: `hub-${uuid || "anon"}` };
}

/**
 * A NEW conversation for this surface, whatever came before.
 *
 * What "nova conversa" is: the old thread is not deleted, it is superseded. The
 * student gets a clean panel AND the agent stops carrying the previous exchange
 * into it — which is the part a purely local reset could not do, since the service
 * remembered a conversation the drawer had thrown away.
 */
export async function rotateThread(
  endpoint: ThreadEndpoint,
  key: ThreadKey
): Promise<AgentThread> {
  const created = await createThread(endpoint, key);

  // If the service would not create one, the student still gets a working chat —
  // it just carries on in the thread they were in. Better than the alternative,
  // which is a turn that errors instead of starting fresh.
  return created ?? fallbackThread(key);
}

async function newestThread(
  endpoint: ThreadEndpoint,
  key: ThreadKey,
  resumeWindowMs?: number
): Promise<AgentThread | null> {
  const query =
    `?agentId=${encodeURIComponent(endpoint.agentId)}` +
    `&metadata=${encodeURIComponent(JSON.stringify(key))}` +
    `&perPage=${THREAD_PAGE}`;

  const result = await request<{
    threads?: Array<{ id?: string; createdAt?: string; updatedAt?: string }>;
  }>(endpoint, `/api/memory/threads${query}`);

  const threads = result.ok ? result.body.threads : null;

  if (!Array.isArray(threads) || threads.length === 0) {
    return null;
  }

  // Sorted here rather than with `orderBy`: one page of a student's own threads is
  // a handful of rows, and this has to agree with the picker's order anyway
  // (queries/conversations.ts), which is the same field.
  //
  // BY LAST ACTIVITY, not by creation. Both orders make a rotation win immediately —
  // a thread created now was also touched now — and they part company the moment the
  // student walks back into an older conversation from the picker and carries on in
  // it. By creation, the next page load would drop them back into a newer thread
  // they had left, while the picker showed the one they were actually using at the
  // top of the list. "Where I was" is the honest default, and it is the one the list
  // in front of them already implies.
  const sorted = [...threads]
    .filter(
      (thread): thread is { id: string; createdAt?: string; updatedAt?: string } =>
        typeof thread.id === "string"
    )
    .sort((a, b) => lastTouched(b).localeCompare(lastTouched(a)));

  const newest = sorted[0];

  if (newest === undefined) {
    return null;
  }

  // Too long untouched, for a surface that expires: reported as "no thread", which
  // sends the caller to create a fresh one. The old one is not deleted — it stays
  // as history, exactly like a thread left behind by "nova conversa".
  if (
    isStale(
      newest.updatedAt,
      resumeWindowMs ?? defaultResumeWindowMs(key)
    )
  ) {
    return null;
  }

  return { id: newest.id };
}

/** When a thread was last worked in, falling back to when it was opened. */
function lastTouched(thread: { createdAt?: string; updatedAt?: string }): string {
  return thread.updatedAt ?? thread.createdAt ?? "";
}

/**
 * Whether a thread has been idle longer than its surface allows.
 *
 * A missing or unparseable timestamp counts as FRESH. The cost of the two mistakes is
 * not symmetric: wrongly resuming means one stale conversation, wrongly expiring
 * throws away a student's exchange over a field we failed to read.
 */
function isStale(updatedAt: string | undefined, windowMs: number | null): boolean {
  if (windowMs === null || updatedAt === undefined) {
    return false;
  }

  const touched = Date.parse(updatedAt);

  return !Number.isNaN(touched) && Date.now() - touched > windowMs;
}

async function createThread(
  endpoint: ThreadEndpoint,
  key: ThreadKey
): Promise<AgentThread | null> {
  const result = await request<{ id?: string }>(
    endpoint,
    `/api/memory/threads?agentId=${encodeURIComponent(endpoint.agentId)}`,
    {
      method: "POST",
      body: JSON.stringify({
        // Required by the endpoint's schema and then IGNORED: the service overrides
        // it with the resource derived from the bearer (mapUserToResourceId).
        // Verified — a thread created while claiming "999" came back owned by the
        // authenticated student. Sending a placeholder rather than a real id keeps
        // this file free of any need to know who the student is.
        resourceId: "self",
        metadata: key,
      }),
    }
  );

  if (!result.ok || typeof result.body.id !== "string") {
    return null;
  }

  return { id: result.body.id };
}

/**
 * The NEWEST page of one thread, oldest first, as the drawer should display them.
 *
 * Where the drawer opens. Everything before this page is read by
 * `fetchMessagePage` as the student scrolls up.
 *
 * THIS IS THE TRANSCRIPT. It used to live in sessionStorage, which made the drawer's
 * history a second copy the agent knew nothing about — so a restored conversation and
 * the agent's own view of it could disagree, and the caderno drawer refused to
 * restore at all rather than show something stale. Reading the thread removes the
 * disagreement by construction: these are the rows the model is given.
 *
 * A MISSING THREAD IS NOT A FAILURE. 404 means the conversation has not happened
 * yet — a thread the service auto-creates on the first turn — so it reads as an
 * empty transcript, not as one we could not load.
 */
export function fetchNewestMessages(
  endpoint: ThreadEndpoint,
  threadId: string
): Promise<ThreadTranscript> {
  // No orderBy on purpose. Left off, the service reads the NEWEST page and hands it
  // back in reading order (memory.recall: `shouldGetNewestAndReverse = !orderBy`).
  // Asking for DESC instead would return the newest page newest-first, i.e. the
  // conversation backwards.
  return fetchMessages(endpoint, threadId, `&page=0`);
}

/**
 * One page of a thread counted FROM THE OLDEST message, for scrolling back.
 *
 * Ascending, and that is the whole point of having a second function. Paging
 * backwards from the newest end sounds simpler and is not stable: every reply the
 * student receives shifts that window by one, so the page above the one on screen
 * would arrive overlapping by however many messages were sent since the drawer
 * opened, and would eventually skip some. Offsets from the START of the thread do
 * not move when a message is appended.
 *
 * Page boundaries are fixed multiples of the page size, so the first page read this
 * way usually overlaps what is already on screen. The caller drops the repeats by
 * id (agent/thread-history.ts) — cheaper than the arithmetic to avoid them, and
 * correct even if the service's idea of a page drifts from ours.
 */
export function fetchMessagePage(
  endpoint: ThreadEndpoint,
  threadId: string,
  page: number
): Promise<ThreadTranscript> {
  const order = JSON.stringify({ field: "createdAt", direction: "ASC" });

  return fetchMessages(
    endpoint,
    threadId,
    `&page=${page}&orderBy=${encodeURIComponent(order)}`
  );
}

async function fetchMessages(
  endpoint: ThreadEndpoint,
  threadId: string,
  window: string
): Promise<ThreadTranscript> {
  const query =
    `?agentId=${encodeURIComponent(endpoint.agentId)}` +
    `&perPage=${HISTORY_PAGE_SIZE}${window}`;

  const result = await request<{
    messages?: unknown[];
    uiMessages?: unknown[];
    total?: unknown;
  }>(endpoint, `/api/memory/threads/${encodeURIComponent(threadId)}/messages${query}`);

  if (!result.ok) {
    return result.reason === "error"
      ? { status: "unreadable" }
      : { status: "read", messages: [], total: 0 };
  }

  // `uiMessages` is in the response schema and is NULL in practice: the route only
  // fills it for gateway agents, and for a Memory-backed one it returns
  // `uiMessages: Array.isArray(...) ? ... : null` over a recall() that does not
  // produce them (checked in @mastra/server's memory handler and @mastra/memory's
  // recall, which returns `get.all.db()`). So the conversion below is not a
  // fallback — it is the normal path. It stays behind the `??` in case a later
  // version starts filling the field.
  const rows = result.body.uiMessages ?? result.body.messages;

  return {
    status: "read",
    messages: Array.isArray(rows) ? toUiMessages(rows) : [],
    // Present in every response the service actually sends — the route spreads the
    // recall result, whose pagination fields ride along with the messages. Read
    // defensively all the same: the documented response schema names only
    // `messages` and `uiMessages`, so a future version could stop sending it, and
    // "how many are there" is not a question worth crashing the drawer over.
    total: typeof result.body.total === "number" ? result.body.total : null,
  };
}

/**
 * The student's last conversations on this surface, newest first.
 *
 * What the picker at the top of the drawer lists. Same metadata filter as
 * `newestThread` — one tag per surface — so a caderno's list is that caderno's
 * conversations and nobody else's: the service resolves the owner from the session
 * and will not list another student's threads whatever we ask for.
 *
 * Null means WE DO NOT KNOW (no service, no session, a 500). The picker shows its
 * plain subtitle then rather than an empty list, which would say the student has
 * never talked to Lume here.
 */
export async function listThreads(
  endpoint: ThreadEndpoint,
  key: ThreadKey,
  limit: number = CONVERSATION_LIMIT
): Promise<ThreadSummary[] | null> {
  // orderBy goes up as JSON — the endpoint's schema JSON-parses the string before
  // validating it, so the structured field/direction pair survives a query string
  const order = JSON.stringify({ field: "updatedAt", direction: "DESC" });
  const query =
    `?agentId=${encodeURIComponent(endpoint.agentId)}` +
    `&metadata=${encodeURIComponent(JSON.stringify(key))}` +
    `&perPage=${limit}` +
    `&orderBy=${encodeURIComponent(order)}`;

  const result = await request<{ threads?: unknown }>(
    endpoint,
    `/api/memory/threads${query}`
  );

  if (!result.ok) {
    // A 404 on the list is "this surface has no conversations", which is a fact
    // about the student and not a failure to read one
    return result.reason === "notFound" ? [] : null;
  }

  const rows = result.body.threads;

  if (!Array.isArray(rows)) {
    return null;
  }

  const threads = rows.map(toSummary).filter(isSummary);

  // Sorted here as well as asked for: `orderBy` is honoured by the store, and this
  // costs nothing over ten rows — but a list in the wrong order puts the wrong
  // conversation at the top of the picker, which is the one thing it must get right
  return threads.sort((a, b) => touchedAt(b).localeCompare(touchedAt(a)));
}

/** When a conversation was last worked on, for sorting. */
function touchedAt(thread: ThreadSummary): string {
  return thread.updatedAt ?? thread.createdAt ?? "";
}

function toSummary(row: unknown): ThreadSummary | null {
  if (row === null || typeof row !== "object") {
    return null;
  }

  const thread = row as {
    id?: unknown;
    title?: unknown;
    createdAt?: unknown;
    updatedAt?: unknown;
  };

  if (typeof thread.id !== "string") {
    return null;
  }

  return {
    id: thread.id,
    title: readTitle(thread.title),
    createdAt: readTimestamp(thread.createdAt),
    updatedAt: readTimestamp(thread.updatedAt),
  };
}

function isSummary(thread: ThreadSummary | null): thread is ThreadSummary {
  return thread !== null;
}

/**
 * A title worth showing, or null.
 *
 * `New Thread 2026-08-31T…` is what Mastra stamps on a thread nobody named, and it
 * is not a title — it is English, it is a timestamp, and ten of them in a row tell
 * the student nothing. Treated as absent, so the picker falls back to the date.
 */
function readTitle(raw: unknown): string | null {
  if (typeof raw !== "string") {
    return null;
  }

  const title = raw.trim();

  return title === "" || title.startsWith("New Thread") ? null : title;
}

function readTimestamp(raw: unknown): string | null {
  if (typeof raw === "string") {
    return raw;
  }

  // Dates come back as strings over JSON, but the response schema types them as
  // Date — so a client that ever gets a real one should not lose it
  return raw instanceof Date ? raw.toISOString() : null;
}

/**
 * The question a conversation opened with, for naming one that has no title.
 *
 * Every thread started from now on is named the moment the student sends into it
 * (components/chat/CadernoChat.tsx), so this is for the ones that were already
 * there when naming did not exist — and for any whose PATCH did not land. Without
 * it the picker is a column of identical dates, which is the one thing a list of
 * past conversations must not be.
 *
 * Five rows and not one: the first row of a thread can be a system reminder or a
 * working-memory write, neither of which is anybody's question. Null when there is
 * no student message in them, which is what an empty conversation looks like.
 */
export async function fetchThreadOpener(
  endpoint: ThreadEndpoint,
  threadId: string
): Promise<string | null> {
  const order = JSON.stringify({ field: "createdAt", direction: "ASC" });
  const query =
    `?agentId=${encodeURIComponent(endpoint.agentId)}` +
    `&perPage=${OPENER_ROWS}&page=0&orderBy=${encodeURIComponent(order)}`;

  const result = await request<{ messages?: unknown[] }>(
    endpoint,
    `/api/memory/threads/${encodeURIComponent(threadId)}/messages${query}`
  );

  if (!result.ok || !Array.isArray(result.body.messages)) {
    return null;
  }

  for (const row of result.body.messages) {
    const text = userText(row);

    if (text !== null) {
      return text;
    }
  }

  return null;
}

/** How far into a thread to look for the student's first words. */
const OPENER_ROWS = 5;

/** One stored row's text, if it is something the student said. */
function userText(row: unknown): string | null {
  if (row === null || typeof row !== "object") {
    return null;
  }

  const stored = row as { role?: unknown; parts?: unknown; content?: unknown };

  if (stored.role !== "user") {
    return null;
  }

  // A user turn is stored as parts like any other, but the oldest rows in a thread
  // can still be a bare string under `content` — both are the same sentence
  if (typeof stored.content === "string" && stored.content.trim() !== "") {
    return stored.content.trim();
  }

  const nested = (stored.content as { parts?: unknown } | null)?.parts;
  const parts = Array.isArray(stored.parts)
    ? stored.parts
    : Array.isArray(nested)
      ? nested
      : [];

  for (const part of parts) {
    const text = (part as { type?: unknown; text?: unknown } | null);

    if (text?.type === "text" && typeof text.text === "string" && text.text.trim() !== "") {
      return text.text.trim();
    }
  }

  return null;
}

/**
 * Names a conversation, so the picker can list it as something other than a date.
 *
 * Called once per thread, with the student's first question in it (see
 * components/chat/CadernoChat.tsx). Their own words rather than a generated
 * summary: it costs no tokens, it is written the moment the thread starts instead
 * of a beat later, and the question a student asked is a fair description of the
 * conversation that follows it.
 *
 * Failure is silent by design — an untitled conversation is listed by date and
 * everything else still works, so there is nothing here worth interrupting a chat
 * for.
 */
export async function renameThread(
  endpoint: ThreadEndpoint,
  threadId: string,
  title: string
): Promise<boolean> {
  const result = await request(
    endpoint,
    `/api/memory/threads/${encodeURIComponent(threadId)}` +
      `?agentId=${encodeURIComponent(endpoint.agentId)}`,
    { method: "PATCH", body: JSON.stringify({ title }) }
  );

  return result.ok;
}

/**
 * Mastra's stored messages → the AI SDK's UIMessage shape.
 *
 * THE TWO SHAPES ARE NOT THE SAME, which is what made restored conversations
 * vanish: a stored row keeps its parts under `content.parts` and a UIMessage keeps
 * them at the top level, so `safeValidateUIMessages` rejected the whole array on
 * the first row and the drawer fell back to its welcome — while the transport went
 * on writing to that same thread. An empty panel over a conversation that was
 * still there.
 *
 * Only what the drawer renders is carried over:
 *
 *   text             kept — the conversation itself
 *   tool-invocation  kept ONLY for the widget tools (agent/tools.ts). A study tool
 *                    is how the agent looked something up, not something to re-show,
 *                    and ToolPart renders null for them anyway.
 *   reasoning        dropped — a tutor's private working, and the drawer has nowhere
 *                    to put it. The transport drops it on the way in too.
 *   step-start       dropped — a boundary between model steps, not content
 *   data-om-status   dropped — observational-memory bookkeeping
 */
function toUiMessages(rows: unknown[]): unknown[] {
  const messages: unknown[] = [];

  for (const row of rows) {
    if (row === null || typeof row !== "object") {
      continue;
    }

    const stored = row as {
      id?: unknown;
      role?: unknown;
      parts?: unknown;
      content?: unknown;
    };

    // System rows are prompt plumbing (working memory, reminders), never turns
    if (stored.role !== "user" && stored.role !== "assistant") {
      continue;
    }

    // Top-level `parts` first, so an already-UI-shaped row passes through
    const nested = (stored.content as { parts?: unknown } | null)?.parts;
    const source = Array.isArray(stored.parts)
      ? stored.parts
      : Array.isArray(nested)
        ? nested
        : [];

    const parts: unknown[] = [];

    for (const part of source) {
      const converted = toUiPart(part);

      if (converted !== null) {
        parts.push(converted);
      }
    }

    // An assistant turn that was nothing but a tool call and its reasoning has
    // nothing left to render, and an empty bubble reads as a failed reply
    if (parts.length === 0) {
      continue;
    }

    messages.push({
      id: typeof stored.id === "string" ? stored.id : `stored-${messages.length}`,
      role: stored.role,
      parts,
    });
  }

  return messages;
}

function toUiPart(raw: unknown): unknown {
  if (raw === null || typeof raw !== "object") {
    return null;
  }

  const part = raw as { type?: unknown; text?: unknown; toolInvocation?: unknown };

  if (part.type === "text") {
    return typeof part.text === "string" && part.text.trim() !== ""
      ? { type: "text", text: part.text }
      : null;
  }

  // Mastra stores tool calls in the v4 shape, nested under `toolInvocation`
  if (part.type === "tool-invocation") {
    return toWidgetPart(part.toolInvocation);
  }

  // Already a UI tool part (a future version returning uiMessages): keep it if we
  // still have a schema for that tool, since the validator would reject it otherwise
  if (typeof part.type === "string" && part.type.startsWith("tool-")) {
    return isWidgetTool(part.type.slice(5)) ? raw : null;
  }

  return null;
}

/** A finished widget call, as a UI tool part. */
function toWidgetPart(invocation: unknown): unknown {
  if (invocation === null || typeof invocation !== "object") {
    return null;
  }

  const call = invocation as {
    toolName?: unknown;
    toolCallId?: unknown;
    state?: unknown;
    args?: unknown;
    result?: unknown;
  };

  if (
    typeof call.toolName !== "string" ||
    typeof call.toolCallId !== "string" ||
    !isWidgetTool(call.toolName)
  ) {
    return null;
  }

  // Only a completed call. A "call"/"partial-call" row is a widget mid-stream from
  // a turn that is over — it would restore as a skeleton that never resolves.
  if (call.state !== "result") {
    return null;
  }

  return {
    type: `tool-${call.toolName}`,
    toolCallId: call.toolCallId,
    state: "output-available",
    input: call.args ?? {},
    output: call.result,
  };
}

/** Widget tools only — the ones the UI has a renderer and a schema for. */
function isWidgetTool(name: string): boolean {
  return Object.prototype.hasOwnProperty.call(lumeTools, name);
}

/**
 * One authenticated call to the service.
 *
 * No failure throws — the drawer must open either way, and an error where a chat
 * should be is worse than a chat with no history. But the failures are TOLD APART,
 * because the caller's decision differs:
 *
 *   unconfigured  no service, or nobody logged in. There is no memory to speak of.
 *   notFound      a thread that has not happened yet. Ordinary, and not an error.
 *   error         a 401, a 500, offline. We do not know what is in the thread, and
 *                 pretending we do is what put an empty panel over a live
 *                 conversation.
 */
type Fetched<T> =
  | { ok: true; body: T }
  | { ok: false; reason: "unconfigured" | "notFound" | "error" };

async function request<T>(
  endpoint: ThreadEndpoint,
  path: string,
  init: RequestInit = {}
): Promise<Fetched<T>> {
  const bearer = lumeBearer();

  if (endpoint.baseUrl === null || endpoint.baseUrl === "" || bearer === null) {
    return { ok: false, reason: "unconfigured" };
  }

  const url = `${endpoint.baseUrl.replace(/\/$/, "")}${path}`;

  try {
    const response = await fetch(url, {
      ...init,
      headers: {
        ...(init.body === undefined ? {} : { "Content-Type": "application/json" }),
        Authorization: `Bearer ${bearer}`,
        ...init.headers,
      },
    });

    if (!response.ok) {
      if (response.status === 404) {
        return { ok: false, reason: "notFound" };
      }

      console.error(`[lume] Memory call failed: ${response.status} ${url}`);

      return { ok: false, reason: "error" };
    }

    return { ok: true, body: (await response.json()) as T };
  } catch (cause) {
    // Logged, unlike before: an unreachable service now changes what the drawer
    // shows, so silence here made that decision unexplainable. The full URL, not
    // just the path: the usual cause is the Mastra service not running at
    // window.__LUME_AGENT_URL__, and the path alone hid which host was tried.
    console.warn(`[lume] Memory unavailable: ${url}`, cause);

    return { ok: false, reason: "error" };
  }
}

/**
 * A question, as a name for the conversation it opened.
 *
 * Shared by the two things that name a thread — the send that starts one, and
 * the repair of one that was never named — so a conversation cannot be listed
 * under one form of its own first question and named after another.
 *
 * Cut at a word, because a title ending mid-word reads as a bug rather than as an
 * abbreviation, and stripped of the question mark a trailing "…" would sit next to.
 */
export function toConversationTitle(text: string): string {
  const question = text.replace(/\s+/g, " ").trim();

  if (question.length <= TITLE_LIMIT) {
    return question;
  }

  const cut = question.slice(0, TITLE_LIMIT);
  const lastSpace = cut.lastIndexOf(" ");

  return `${(lastSpace > TITLE_LIMIT / 2 ? cut.slice(0, lastSpace) : cut).replace(
    /[\s,.;:?!]+$/,
    ""
  )}…`;
}

/**
 * How long a conversation's name may be.
 *
 * Sized for the picker, where a row gives a title two lines of about thirty
 * characters — so anything past this is invisible there and would only make the
 * header's single line truncate sooner.
 */
const TITLE_LIMIT = 60;
